import { useState } from 'react';
import { Heart, Globe, Users, BookOpen, Sparkles, Sun, Star, Flame, Cross, Award, Crown, Church, Leaf, Plus, ChevronUp, ChevronDown, Trash2, Loader2, Eye, EyeOff } from 'lucide-react';
import { useEditMode } from '@/context/EditModeContext';
import { useSiteData } from '@/context/SiteDataContext';
import api from '@/lib/api';
import { toast } from 'sonner';

// -----------------------------------------------------------------------------
// PillarItems
// -----------------------------------------------------------------------------
// Renders the array-driven Four Pillars section. Admins can add, edit, hide,
// reorder, remove pillars inline (edit mode). Data lives at
// page_content.home.pillars_items = [{id, order, title, text, icon, visible}].
// -----------------------------------------------------------------------------

const ICONS = { Heart, Globe, Users, BookOpen, Sparkles, Sun, Star, Flame, Cross, Award, Crown, Church, Leaf };
const ICON_KEYS = Object.keys(ICONS);

export default function PillarItems({ items: itemsProp }) {
  const { editMode } = useEditMode();
  const { loadPageContent } = useSiteData();
  const [busy, setBusy] = useState(false);
  const items = (itemsProp || []).slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  const visible = editMode ? items : items.filter(i => i.visible !== false);

  const persist = async (next) => {
    setBusy(true);
    try {
      await api.patch('/site/page-content/home', { pillars_items: next });
      await loadPageContent('home');
      toast.success('Saved');
    } catch { toast.error('Save failed'); } finally { setBusy(false); }
  };
  const addOne = () => {
    const order = items.length ? Math.max(...items.map(i => i.order ?? 0)) + 1 : 0;
    persist([...items, { id: `pillar_${Date.now()}`, order, title: 'New Pillar', text: 'Describe this pillar.', icon: 'Star', visible: true }]);
  };
  const updateOne = (id, patch) => persist(items.map(i => i.id === id ? { ...i, ...patch } : i));
  const removeOne = (id) => { if (!window.confirm('Remove this pillar?')) return; persist(items.filter(i => i.id !== id)); };
  const move = (id, delta) => {
    const idx = items.findIndex(i => i.id === id);
    const swap = idx + delta;
    if (swap < 0 || swap >= items.length) return;
    const next = [...items];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    persist(next.map((it, i) => ({ ...it, order: i })));
  };

  const gridCols = visible.length <= 2 ? 'md:grid-cols-2' : (visible.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4');

  return (
    <>
      <div className={`grid ${gridCols} gap-6`}>
        {visible.map((p, i) => {
          const Icon = ICONS[p.icon] || Star;
          const hidden = p.visible === false;
          return (
            <div key={p.id} data-testid={`pillar-item-${p.id}`}
                 className={`card-tactile p-8 relative ${hidden ? 'opacity-40 ring-2 ring-red-300' : ''}`}>
              <div className="w-14 h-14 rounded-full bg-royal/5 flex items-center justify-center mb-6"><Icon className="w-6 h-6 text-royal" /></div>
              {editMode ? (
                <>
                  <input value={p.title || ''} data-testid={`pillar-title-${p.id}`}
                    onChange={(e) => updateOne(p.id, { title: e.target.value })}
                    className="w-full font-serif text-2xl text-royal font-bold mb-3 border-b border-gold/30 bg-transparent focus:outline-none" />
                  <textarea rows={3} value={p.text || ''} data-testid={`pillar-text-${p.id}`}
                    onChange={(e) => updateOne(p.id, { text: e.target.value })}
                    className="w-full text-charcoal-muted bg-transparent focus:outline-none resize-y" />
                  <div className="flex flex-wrap items-center gap-1 mt-3 pt-3 border-t border-gold/20">
                    <select value={p.icon || 'Star'} onChange={(e) => updateOne(p.id, { icon: e.target.value })}
                      data-testid={`pillar-icon-${p.id}`}
                      className="text-xs px-2 py-1 border rounded">
                      {ICON_KEYS.map(k => <option key={k}>{k}</option>)}
                    </select>
                    <button title="Move left" onClick={() => move(p.id, -1)} disabled={i === 0 || busy} className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                    <button title="Move right" onClick={() => move(p.id, 1)} disabled={i === visible.length - 1 || busy} className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                    <button title={hidden ? 'Show' : 'Hide'} onClick={() => updateOne(p.id, { visible: hidden })} className="p-1 hover:bg-gray-100 rounded">
                      {hidden ? <EyeOff className="w-3.5 h-3.5 text-red-500" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                    <button title="Remove" onClick={() => removeOne(p.id)} disabled={busy} className="p-1 text-red-500 hover:bg-red-50 rounded ml-auto"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="font-serif text-2xl text-royal font-bold mb-3">{p.title}</h3>
                  <p className="text-charcoal-muted leading-relaxed">{p.text}</p>
                </>
              )}
            </div>
          );
        })}
      </div>
      {editMode && (
        <button onClick={addOne} disabled={busy} data-testid="pillar-add-btn"
          className="mt-6 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-royal text-white text-sm font-semibold hover:bg-gold hover:text-royal-dark transition disabled:opacity-60">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Add pillar
        </button>
      )}
    </>
  );
}
